<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const route = useRoute();
const { t } = useI18n();
const loading = ref(true);
const report = ref({});
const period = ref('90');

const formatAmount = value =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const reportPeriod = computed(() => {
  const { from, to } = report.value.period || {};
  if (!from || !to) return t('SALES.PERIOD_EMPTY');
  return `${new Date(from).toLocaleDateString()} – ${new Date(to).toLocaleDateString()}`;
});

const metrics = computed(() =>
  [
    ['GROSS_REVENUE', formatAmount(report.value.gross_revenue)],
    ['NET_REVENUE', formatAmount(report.value.net_revenue)],
    ['FEE_REVENUE', formatAmount(report.value.fee_revenue)],
    ['AVERAGE_TICKET', formatAmount(report.value.average_ticket)],
    ['COUNT', report.value.count || 0],
    ['PENDING_COUNT', report.value.pending_count || 0],
    ['REFUNDS', formatAmount(report.value.refunds)],
    ['CHARGEBACKS', formatAmount(report.value.chargebacks)],
  ].map(([key, value]) => ({ key, label: t(`SALES.${key}`), value }))
);

const series = computed(() => {
  const values = Object.entries(report.value.timeseries || {});
  const max = Math.max(...values.map(([, value]) => Number(value)), 1);
  return values.map(([date, value]) => ({
    date,
    value: Number(value),
    height: `${Math.max((Number(value) / max) * 100, 3)}%`,
  }));
});

const providerRows = computed(() =>
  Object.entries(report.value.by_provider || {}).sort(([, a], [, b]) => b - a)
);

const offerRows = computed(() =>
  Object.entries(report.value.by_offer || {})
    .map(([name, value]) => [name || t('SALES.UNKNOWN_OFFER'), value])
    .sort(([, a], [, b]) => b - a)
);

const load = async () => {
  loading.value = true;
  const since = new Date();
  since.setDate(since.getDate() - Number(period.value));
  const response = await window.axios.get(
    `/api/v2/accounts/${route.params.accountId}/sales_report`,
    { params: { since: since.toISOString(), until: new Date().toISOString() } }
  );
  report.value = response.data;
  loading.value = false;
};

onMounted(load);
</script>

<template>
  <main class="min-h-full bg-n-surface-2 p-8">
    <header class="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <p class="mb-1 text-sm text-n-slate-11">{{ t('SALES.OVERVIEW') }}</p>
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('SALES.REPORT_TITLE') }}
        </h1>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ t('SALES.PERIOD_LABEL', { period: reportPeriod }) }}
        </p>
      </div>
      <select
        v-model="period"
        class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        @change="load"
      >
        <option value="7">{{ t('SALES.PERIOD_7_DAYS') }}</option>
        <option value="30">{{ t('SALES.PERIOD_30_DAYS') }}</option>
        <option value="90">{{ t('SALES.PERIOD_90_DAYS') }}</option>
      </select>
    </header>

    <p v-if="loading" class="text-sm text-n-slate-11">
      {{ t('SALES.LOADING') }}
    </p>
    <template v-else>
      <section
        class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        <article
          v-for="metric in metrics"
          :key="metric.key"
          class="rounded-xl border border-n-weak bg-n-surface-1 p-5"
        >
          <p class="text-sm text-n-slate-11">{{ metric.label }}</p>
          <p class="mt-3 text-2xl font-semibold text-n-slate-12">
            {{ metric.value }}
          </p>
        </article>
      </section>

      <section class="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <article
          class="rounded-xl border border-n-weak bg-n-surface-1 p-6 xl:col-span-2"
        >
          <div class="mb-6 flex items-center justify-between">
            <h2 class="font-semibold text-n-slate-12">
              {{ t('SALES.GROSS_REVENUE') }}
            </h2>
            <span class="text-xs text-n-slate-10">{{ t('SALES.DATE') }}</span>
          </div>
          <div class="flex h-56 items-end gap-1 border-b border-n-weak">
            <div
              v-for="item in series"
              :key="item.date"
              class="group flex h-full flex-1 items-end"
            >
              <div
                class="relative w-full rounded-t bg-n-brand transition-colors group-hover:brightness-110"
                :style="{ height: item.height }"
                :title="`${item.date}: ${formatAmount(item.value)}`"
              />
            </div>
          </div>
          <div class="mt-2 flex justify-between text-xs text-n-slate-10">
            <span>{{ series[0]?.date }}</span>
            <span>{{ series[series.length - 1]?.date }}</span>
          </div>
        </article>

        <article class="rounded-xl border border-n-weak bg-n-surface-1 p-6">
          <h2 class="mb-5 font-semibold text-n-slate-12">
            {{ t('SALES.PROVIDER') }}
          </h2>
          <div v-if="providerRows.length" class="space-y-4">
            <div v-for="row in providerRows" :key="row[0]">
              <div class="mb-1 flex justify-between text-sm">
                <span class="text-n-slate-11">{{ row[0] }}</span>
                <span class="font-medium text-n-slate-12">{{
                  formatAmount(row[1])
                }}</span>
              </div>
              <div class="h-2 rounded-full bg-n-alpha-2">
                <div
                  class="h-2 rounded-full bg-n-brand"
                  :style="{ width: `${(row[1] / providerRows[0][1]) * 100}%` }"
                />
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-n-slate-11">{{ t('SALES.EMPTY') }}</p>
        </article>
      </section>

      <section class="mt-6 rounded-xl border border-n-weak bg-n-surface-1 p-6">
        <h2 class="mb-5 font-semibold text-n-slate-12">
          {{ t('SALES.SALES_BY_OFFER') }}
        </h2>
        <div
          v-if="offerRows.length"
          class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
        >
          <div
            v-for="row in offerRows"
            :key="row[0]"
            class="flex items-center justify-between border-b border-n-weak py-3 text-sm"
          >
            <span class="text-n-slate-11">{{ row[0] }}</span>
            <span class="font-medium text-n-slate-12">{{
              formatAmount(row[1])
            }}</span>
          </div>
        </div>
        <p v-else class="text-sm text-n-slate-11">{{ t('SALES.EMPTY') }}</p>
      </section>
    </template>
  </main>
</template>
